import { useState, useEffect, useRef } from 'react';

export const useDocumentType = (fileUrl: string, fileName?: string) => {
  const [fileType, setFileType] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const lastValidTypeRef = useRef<string | null>(null);
  const prevValuesRef = useRef({ fileUrl: '', fileName: '' });

  useEffect(() => {
    // Skip if values haven't changed
    if (prevValuesRef.current.fileUrl === fileUrl && prevValuesRef.current.fileName === (fileName || '')) {
      return;
    }
    
    prevValuesRef.current = { fileUrl, fileName: fileName || '' };
    
    let extension: string | undefined;
    
    const isBlobOrDataUrl = fileUrl.startsWith('blob:') || fileUrl.startsWith('data:');
    
    if (isBlobOrDataUrl && fileName) {
      extension = fileName.split('.').pop()?.toLowerCase();
    } else {
      // For regular URLs, try to get extension from URL
      const urlParts = fileUrl.split('?')[0].split('.'); // Remove query params first
      if (urlParts.length > 1) {
        extension = urlParts.pop()?.toLowerCase();
      }
      
      // If no valid extension found in URL and fileName is provided, use fileName
      if (!extension && fileName) {
        extension = fileName.split('.').pop()?.toLowerCase();
      }
    }
    
    let detectedType: string | null = null;
    
    if (extension === 'pdf') {
      detectedType = 'pdf';
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')) {
      detectedType = 'image';
      setNumPages(1); // Images are single "page"
    }
    
    // If we detected a type, update both state and ref
    if (detectedType) {
      lastValidTypeRef.current = detectedType;
      setFileType(detectedType);
    } else if (lastValidTypeRef.current) {
      // Keep the last valid type if we can't determine it now
      setFileType(lastValidTypeRef.current);
    } else {
      setFileType(null);
    }
  }, [fileUrl, fileName]);

  return { fileType, numPages, setNumPages };
};